import React, { useCallback, useState } from 'react';

import { DownloadIcon, ZoomInIcon } from '@vlabs/icons';
import { downloadImage } from '@vlabs/monokit/shared/lib/images';
import type { DetectionOverlaySettingsValue } from '@vlabs/monokit/shared/ui/detection-overlay';
import { DetectionOverlaySettings } from '@vlabs/monokit/shared/ui/detection-overlay';
import { FullFrameWithDetections } from '@vlabs/monokit/shared/ui/full-frame-with-detections';
import {
  useVmsFromSourceName,
  VideoArchiveWidget,
} from '@vlabs/monokit/widgets/video-archive';
import {
  ButtonSwitch, ButtonSwitchGroup, ButtonScrimGroup, useModal,
} from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton';
import {
  getSavedVideoAnalyticOverlayColor,
  saveVideoAnalyticOverlayColor,
} from '@vlapps/point/src/utils/videoAnalyticOverlayColor';
import type { VideoAnalyticOverlayColor } from '@vlapps/point/src/utils/videoAnalyticOverlayColor';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { selectVAStreamById } from '@vlabs/pages/video-analytics/selectors';

import st from './ImageBlock.module.sass';

type DetectionType = 'point' | 'bbox';
type OverlayDetection = Record<string, unknown>;

interface VideoAnalyticStream {
  name?: string;
}

interface FullFrameImage {
  url?: string | null;
}

interface ImageBlockOwnProps {
  eventType?: string;
  detections?: OverlayDetection[];
  detectionType: DetectionType | undefined;
  createTime?: string;
  fullFrame: FullFrameImage;
  streamId?: string;
}

interface ReduxState {
  videoAnalytics?: unknown;
}

const TABS = {
  video: 'video',
  frame: 'frame',
} as const;

type ImageBlockTab = typeof TABS[keyof typeof TABS];

const connector = connect((state: ReduxState) => ({
  getStreamById: (id?: string) => (id ? selectVAStreamById(state, id) as VideoAnalyticStream | undefined : undefined),
}));

function ImageBlockComponent({
  eventType = '',
  detections = [],
  detectionType,
  createTime = '',
  fullFrame,
  streamId = '',
  getStreamById,
}: ImageBlockOwnProps & {
  getStreamById: (id?: string) => VideoAnalyticStream | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<ImageBlockTab>(TABS.frame);
  const [color, setColor] = useState(getSavedVideoAnalyticOverlayColor);
  const [dotSize, setDotSize] = useState<number | undefined>();
  const [isDetectionVisible, setIsDetectionVisible] = useState(true);

  const fullFrameModal = useModal({ className: [st.FullFrameModal] });

  const currentStreamName = getStreamById(streamId)?.name;
  const { vmsSource } = useVmsFromSourceName(currentStreamName);

  const handleColorChange = useCallback((nextColor: VideoAnalyticOverlayColor) => {
    setColor(nextColor);
    saveVideoAnalyticOverlayColor(nextColor);
  }, []);
  const handleChangeSettings = useCallback((nextSettings: DetectionOverlaySettingsValue) => {
    setIsDetectionVisible(nextSettings.withDetections);
    setDotSize(nextSettings.dotSize);

    if (nextSettings.color) {
      handleColorChange(nextSettings.color);
    }
  }, [handleColorChange]);

  return (
    <div>
      {fullFrameModal.wrap(
        <FullFrameWithDetections
          className={st.FullFrameClassName}
          data-testid="videoAnalytics.details"
          detections={{
            type: detectionType,
            coordinates: detections,
            color,
            dotSize,
          }}
          src={fullFrame?.url ?? undefined}
          withDetections={isDetectionVisible}
        />,
      )}
      {!!vmsSource && (
        <ButtonSwitchGroup<ImageBlockTab>
          className={st.SwitchGroup}
          data-testid="topMatchSwitch"
          onChange={setActiveTab}
          size="small"
          value={activeTab}
          variant="square"
        >
          <ButtonSwitch value="frame">
            {t('videoAnalytics:details.toggle buttons.frame')}
          </ButtonSwitch>
          <ButtonSwitch value="video">
            {t('videoAnalytics:details.toggle buttons.video')}
          </ButtonSwitch>
        </ButtonSwitchGroup>
      )}
      {TABS.frame === activeTab && (
        <div className={st.FrameBlock}>
          <div
            className={st.FullFrameContainer}
            data-testid="FullFrameContainer"
          >
            <img
              alt="backgroundImage"
              className={st.BackgroundImage}
              role="presentation"
              src={fullFrame.url ?? undefined}
            />
            <FullFrameWithDetections
              className={st.FullFrameClassName}
              detections={{
                type: detectionType,
                coordinates: detections,
                color,
                dotSize,
              }}
              src={fullFrame?.url ?? undefined}
              withDetections={isDetectionVisible}
            />
            {!!fullFrame.url && (
              <ButtonScrimGroup
                className={st.Actions}
                size="small"
              >
                <IconButton
                  data-testid="openFullScreenModalButton"
                  icon={<ZoomInIcon />}
                  kind="secondary"
                  onClick={() => fullFrameModal.open()}
                  size="small"
                  variant="flat"
                />
                <IconButton
                  data-testid="videoAnalytics.details.downloadButton"
                  icon={<DownloadIcon />}
                  kind="secondary"
                  onClick={() => downloadImage(fullFrame.url)}
                  size="small"
                  variant="flat"
                />
              </ButtonScrimGroup>
            )}
          </div>

          {!!detectionType && !!fullFrame.url && (
            <div className={st.ControlsOverlay}>
              <DetectionOverlaySettings
                label={t(`videoAnalytics:details.${eventType}.detection switch.label`) ?? ''}
                onChangeSettings={handleChangeSettings}
                settings={{
                  withDetections: isDetectionVisible,
                  withColor: true,
                  color,
                  withSize: detectionType === 'point',
                  dotSize,
                }}
              />
            </div>
          )}
        </div>
      )}
      {TABS.video === activeTab && (
        <VideoArchiveWidget
          startTime={createTime}
          vmsSource={vmsSource}
        />
      )}
    </div>
  );
}

export const ImageBlock = connector(ImageBlockComponent);
